import { useEffect, useState, type ReactElement } from 'react'
import type {
  BoardUnit,
  CombineLocation,
  ShopSlot,
  ShopState,
  UnitDef,
} from '@origami/shared'
import { applyCombine, canCombine } from '@origami/shared'
import { Shop } from '../game/Shop'
import { ShopTutorial } from './ShopTutorial'
import { buildMockShopState } from '../game/shopMock'

type FetchState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; state: ShopState; catalog: UnitDef[] }

/**
 * ShopTestScreen — design-time harness for the shop UI at /?shop.
 *
 * Fetches the unit catalog from /api/units, hands it to
 * buildMockShopState() to fabricate a "round 4" snapshot, mounts <Shop>
 * with action handlers that mutate local state + play
 * EvolutionSequence as a fixed overlay on star-up.
 *
 * Replace this with a real store/socket-connected screen once the
 * shop state machine ships.
 */
export function ShopTestScreen(): ReactElement {
  const [state, setState] = useState<FetchState>({ kind: 'loading' })
  // Harness host for the shop tutorial — lets /?shop exercise the
  // overlay (the "?" button) at every viewport without an auth session.
  const [tutorialOpen, setTutorialOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/units')
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return (await r.json()) as UnitDef[]
      })
      .then((catalog) => {
        if (cancelled) return
        // Tokens are simulator-only — never shoppable.
        const shoppable = catalog.filter((u) => !u.is_token)
        setState({
          kind: 'ready',
          state: buildMockShopState(shoppable),
          catalog: shoppable,
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setState({
          kind: 'error',
          message: err instanceof Error ? err.message : String(err),
        })
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (state.kind === 'loading') {
    return (
      <div className="paper" style={{ minHeight: '100vh', padding: 40 }}>
        <p className="hand-note" style={{ textAlign: 'center' }}>
          Loading shop…
        </p>
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <div className="paper" style={{ minHeight: '100vh', padding: 40 }}>
        <div
          className="cute-card"
          style={{
            maxWidth: 420,
            margin: '40px auto',
            padding: 24,
            textAlign: 'center',
          }}
        >
          <div className="cute-title" style={{ fontSize: 22 }}>
            Couldn't load shop
          </div>
          <div className="hand-note" style={{ marginTop: 8 }}>
            {state.message}
          </div>
        </div>
      </div>
    )
  }

  function handleCombine(from: CombineLocation, to: CombineLocation): void {
    setState((prev) => {
      if (prev.kind !== 'ready') return prev
      const sourceUnit = locUnit(prev.state, from)
      const targetUnit = locUnit(prev.state, to)
      if (!sourceUnit || !targetUnit) return prev
      let combine
      try {
        combine = applyCombine(sourceUnit, targetUnit)
      } catch {
        return prev
      }
      let next = withSlot(
        withSlot(prev.state, to, combine.result),
        from,
        null,
      )
      if (combine.refund > 0) {
        next = { ...next, gold: next.gold + combine.refund }
      }
      if (
        combine.starUp &&
        countActiveBonusSlots(next.shopSlots) < MAX_BONUS_SLOTS
      ) {
        const bonus = makeBonusSlot(prev.catalog, combine.result.unit.tier)
        if (bonus) {
          next = { ...next, shopSlots: [...next.shopSlots, bonus] }
        }
      }
      // EvolutionSequence overlay is owned by Shop itself now; the
      // test-screen mock just mutates state in lockstep.
      return { ...prev, state: next }
    })
  }

  function handleMove(from: CombineLocation, to: CombineLocation): void {
    setState((prev) => {
      if (prev.kind !== 'ready') return prev
      const sourceUnit = locUnit(prev.state, from)
      if (!sourceUnit) return prev
      // empty target — just relocate the unit
      const next = withSlot(withSlot(prev.state, from, null), to, sourceUnit)
      return { ...prev, state: next }
    })
  }

  /**
   * Buy a shop slot and place its unit on the board. If `target` is
   * provided (drag flow), the unit lands at that exact location and
   * combines with whatever is there if compatible. If `target` is
   * omitted (click flow), the unit auto-places into the first empty
   * bench slot, falling back to the first empty active slot.
   */
  function purchase(slotIndex: number, target?: CombineLocation): void {
    setState((prev) => {
      if (prev.kind !== 'ready') return prev
      const slot = prev.state.shopSlots[slotIndex]
      if (!slot || slot.bought) return prev
      const cost = slot.isBonus ? 0 : slot.cost
      if (prev.state.gold < cost) return prev

      // Resolve target on the click path: prefer empty bench, then active.
      let resolved = target
      if (!resolved) {
        const benchIdx = prev.state.bench.findIndex((u) => u === null)
        if (benchIdx >= 0) {
          resolved = { kind: 'bench', index: benchIdx }
        } else {
          const activeIdx = prev.state.activeLine.findIndex((u) => u === null)
          if (activeIdx >= 0) {
            resolved = { kind: 'active', index: activeIdx }
          } else {
            return prev // no room — silent reject
          }
        }
      }

      const fresh: BoardUnit = {
        unit: slot.unit,
        star: 1,
        fill: 1,
        hp: slot.unit.stats.hp,
        atk: slot.unit.stats.atk,
      }
      const target_unit = locUnit(prev.state, resolved)

      let next = prev.state
      let refund = 0
      let starUp = false
      if (!target_unit) {
        next = withSlot(next, resolved, fresh)
      } else if (canCombine(fresh, target_unit) === true) {
        let combine
        try {
          combine = applyCombine(fresh, target_unit)
        } catch {
          return prev
        }
        next = withSlot(next, resolved, combine.result)
        refund = combine.refund
        starUp = combine.starUp
        // Shop owns the EvolutionSequence overlay now — the
        // test-screen only mutates state in lockstep.
      } else {
        return prev // invalid target unit — drag-feedback should prevent this
      }

      // Bonus slots disappear when bought (don't reserve a grid column);
      // base slots stay as bought-vacant placeholders so the user sees
      // their fixed positions until reroll.
      const newSlots = slot.isBonus
        ? prev.state.shopSlots.filter((_, i) => i !== slotIndex)
        : prev.state.shopSlots.map((s, i) =>
            i === slotIndex ? { ...s, bought: true } : s,
          )
      if (
        starUp &&
        countActiveBonusSlots(newSlots) < MAX_BONUS_SLOTS
      ) {
        // Use the result unit's tier — the combined result is what
        // determines bonus tier per spec §5.
        const bonus = makeBonusSlot(prev.catalog, slot.unit.tier)
        if (bonus) newSlots.push(bonus)
      }
      next = {
        ...next,
        shopSlots: newSlots,
        gold: prev.state.gold - cost + refund,
      }
      return { ...prev, state: next }
    })
  }

  function handleSell(where: 'active' | 'bench', index: number): void {
    setState((prev) => {
      if (prev.kind !== 'ready') return prev
      const loc: CombineLocation = { kind: where, index }
      const sold = locUnit(prev.state, loc)
      if (!sold) return prev
      // Sell value scales with achieved star: tier × star. Incomplete
      // fill (progress toward the NEXT star) does not count — only
      // reached tiers pay back.
      const refund = sold.unit.tier * sold.star
      const next = {
        ...withSlot(prev.state, loc, null),
        gold: prev.state.gold + refund,
      }
      return { ...prev, state: next }
    })
  }

  return (
    <>
      <Shop
        state={state.state}
        actions={{
          onBuy: (i) => purchase(i),
          onBuyToSlot: (i, target) => purchase(i, target),
          onSell: handleSell,
          onCombine: handleCombine,
          onMove: handleMove,
          onReady: () => console.info('[shop test] onReady'),
          onOpenTutorial: () => setTutorialOpen(true),
          onExit: () => {
            window.history.pushState(null, '', '?')
            window.dispatchEvent(new Event('url-changed'))
          },
          onReroll: () => {
            setState((prev) => {
              if (prev.kind !== 'ready') return prev
              if (prev.state.gold < prev.state.rerollCost) return prev
              const slots = prev.state.shopSlots.map((slot) =>
                rerollSlot(slot, prev.catalog, prev.state.shopLevel),
              )
              return {
                ...prev,
                state: {
                  ...prev.state,
                  shopSlots: slots,
                  gold: prev.state.gold - prev.state.rerollCost,
                },
              }
            })
          },
          onToggleSlotLock: (slotIndex) => {
            setState((prev) => {
              if (prev.kind !== 'ready') return prev
              const slot = prev.state.shopSlots[slotIndex]
              if (!slot) return prev
              const slots = prev.state.shopSlots.slice()
              slots[slotIndex] = { ...slot, locked: !slot.locked }
              return { ...prev, state: { ...prev.state, shopSlots: slots } }
            })
          },
        }}
      />
      <ShopTutorial open={tutorialOpen} onClose={() => setTutorialOpen(false)} />
    </>
  )
}

// === helpers ===

function locUnit(state: ShopState, loc: CombineLocation): BoardUnit | null {
  return loc.kind === 'active'
    ? state.activeLine[loc.index] ?? null
    : state.bench[loc.index] ?? null
}

function withSlot(
  state: ShopState,
  loc: CombineLocation,
  unit: BoardUnit | null,
): ShopState {
  if (loc.kind === 'active') {
    const next = state.activeLine.slice()
    next[loc.index] = unit
    return { ...state, activeLine: next }
  }
  const next = state.bench.slice()
  next[loc.index] = unit
  return { ...state, bench: next }
}

/**
 * Reroll one shop slot. Bonus slots are preserved per spec §5
 * ("Reroll does not affect bonus slots"). Locked slots with visible
 * content also persist; locked-and-bought slots are an edge case
 * (UI doesn't currently allow locking a vacant slot, but data could
 * permit it) — those regenerate so the slot doesn't stay empty.
 *
 * Unit pool: any unit with tier ≤ shopLevel, picked uniformly at
 * random. A real shop will later read shop_distribution.json for
 * per-tier probability weights.
 */
function rerollSlot(
  slot: ShopSlot,
  catalog: UnitDef[],
  shopLevel: number,
): ShopSlot {
  if (slot.isBonus) return slot
  if (slot.locked && !slot.bought) return slot
  const pool = catalog.filter((u) => u.tier <= shopLevel)
  if (pool.length === 0) return slot
  const pick = pool[Math.floor(Math.random() * pool.length)]!
  return { unit: pick, cost: pick.tier }
}

/** Max simultaneous ACTIVE bonus slots per spec §5. Bought bonus
 *  slots don't count — once a player claims the free unit, the
 *  cap frees up for the next star-up to fill. */
const MAX_BONUS_SLOTS = 1

/**
 * Build a free bonus slot per spec §5 — appears each time a combine
 * triggers a star-up (subject to MAX_BONUS_SLOTS).
 *
 * Bonus unit is exactly **one tier above the combined unit's tier**,
 * capped at tier 5. So combining a tier-3 unit drops a free tier-4;
 * combining a tier-5 unit gives another free tier-5. Falls back to
 * the catalog's highest available tier when no unit at the strict
 * target tier exists (handles sparse catalogs in early dev).
 *
 * NOTE: per spec §5 bonus slots vanish at the end of the shop phase
 * (round transition). The test harness has no round-transition
 * wiring yet; cleanup will live with the future round-state machine.
 */
function makeBonusSlot(
  catalog: UnitDef[],
  combinedUnitTier: number,
): ShopSlot | null {
  const targetTier = Math.min(combinedUnitTier + 1, 5)
  let pool = catalog.filter((u) => u.tier === targetTier)
  if (pool.length === 0) {
    // No unit at the strict target tier — widen to "anything above
    // the combined unit's tier", then fall back to catalog max if
    // nothing higher exists.
    pool = catalog.filter((u) => u.tier > combinedUnitTier)
  }
  if (pool.length === 0) {
    const maxTier = Math.max(...catalog.map((u) => u.tier))
    pool = catalog.filter((u) => u.tier === maxTier)
  }
  if (pool.length === 0) return null
  const pick = pool[Math.floor(Math.random() * pool.length)]!
  return { unit: pick, cost: 0, isBonus: true }
}

/** Active bonus slots = those still unclaimed. Once bought, the
 *  slot is vacant and frees up cap room. */
function countActiveBonusSlots(slots: ShopSlot[]): number {
  return slots.reduce((n, s) => (s.isBonus && !s.bought ? n + 1 : n), 0)
}

